import React, { useState, useEffect } from "react";
import { Modal, Input, Tag, Spin, message } from "antd";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { fetchUnavailableQuickReply, injectUnavailablePartsTemplate } from "./helpers";

// Standalone "let the customer know some part(s) couldn't be sourced" notice —
// usable whenever a task has parts nobody quoted, independent of the task's
// overall status. Same backend endpoint regardless of where it's opened from
// (mark-as-lost, the task drawer button, or the post-quote-share nudge).
export function UnavailablePartsModal({ open, queryPk, queryId, customerName, customerPhone, unavailableParts, userId, onClose, onSuccess }) {
  const [messageText, setMessageText] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTemplateLoading(true);
    fetchUnavailableQuickReply()
      .then(template => {
        if (!template) { setMessageText(""); return; }
        setMessageText(injectUnavailablePartsTemplate(template.text, {
          customerName,
          queryId,
          parts: unavailableParts,
        }));
      })
      .finally(() => setTemplateLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSend = async () => {
    if (!messageText.trim()) { message.warning("Message can't be empty"); return; }
    if (!customerPhone) { message.error("No phone number on file for this customer"); return; }
    setSending(true);
    try {
      const res = await axios.post(
        `${API_DOMAIN}/queries/${queryPk}/notify-unavailable-parts`,
        {
          userId,
          message_text: messageText.trim(),
          part_ids: (unavailableParts || []).map(p => p.id ?? p.query_part_id).filter(Boolean),
        },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data?.success) throw new Error(res.data?.message);
      message.success("Customer notified");
      onSuccess?.();
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`Notify Customer — Unavailable Parts${queryId ? ` — #${queryId}` : ""}`}
      onCancel={onClose}
      onOk={handleSend}
      confirmLoading={sending}
      okText="Send to Customer"
      cancelText="Cancel"
      destroyOnClose
      width={520}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
        <div style={{ fontSize: 13, color: "#595959" }}>
          {customerName || "This customer"} will be told the part(s) below couldn't be sourced.
          No approval is needed — this is a courtesy notice, not a quote.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(unavailableParts || []).map((p, i) => (
            <Tag key={p.id ?? p.query_part_id ?? i} color="orange">{p.part_name || `Part ${i + 1}`}</Tag>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#595959" }}>
            Message <span style={{ color: "#ff4d4f" }}>*</span>
          </div>
          {templateLoading ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}><Spin /></div>
          ) : (
            <Input.TextArea
              rows={9}
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              placeholder="Message to send"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
